import { useEffect, useState } from 'react';

import {
  WaveformPeaks,
  peaksCache,
  fetchPeaksJson,
  decodePeaksFromMedia,
} from './peaks';

/**
 * 波形ピークデータ取得フック（全オーディオプレビュー共有）。
 *
 * 1. `waveformUrl`（Lambda 事前計算の peaks JSON）があれば fetch して使う
 * 2. JSON が無い・取得に失敗した場合は `fallbackSrc`（元メディア URL）を
 *    ブラウザ側で decodeAudioData し、min/max ピークを自前計算する
 *    （波形未生成の既存アップロードや Lambda が動かないローカル環境向け）
 *
 * 結果はモジュールスコープのキャッシュに保持し、再マウントで再フェッチしない。
 * 多数並ぶ画面（メッセージ一覧等）では useVisibleOnce と組み合わせ、
 * 可視化されてから `fallbackSrc` を渡すことで一斉デコードを避ける。
 */

export interface PeaksResult {
  /** null = 未取得（ロード中 or 失敗） */
  peaks: WaveformPeaks | null;
  isLoading: boolean;
  /** 取得・デコード失敗（プレースホルダー表示用） */
  error: boolean;
}

/** useMultiPeaks の 1 リクエスト分 */
export interface PeaksRequest {
  /** キャッシュキー（メディアアイテム ID 等） */
  key: string;
  /** Lambda 事前計算の peaks JSON URL */
  waveformUrl?: string | null;
  /** decode フォールバック用の元メディア URL（HLS 不可のため original_url を渡す） */
  fallbackSrc?: string | null;
}

/**
 * peaks の取得チェーン: waveformUrl の JSON を優先し、
 * 無い場合・取得失敗した場合は fallbackSrc の decode にフォールバックする。
 */
async function resolvePeaks(
  waveformUrl: string | null | undefined,
  fallbackSrc: string | null | undefined,
  signal: AbortSignal,
): Promise<WaveformPeaks> {
  if (waveformUrl) {
    try {
      return await fetchPeaksJson(waveformUrl, signal);
    } catch (e) {
      if (signal.aborted || !fallbackSrc) throw e;
      console.warn('[usePeaks] 波形JSONの取得に失敗、decode にフォールバック:', e);
    }
  }
  return decodePeaksFromMedia(fallbackSrc as string, signal);
}

export function usePeaks(params: {
  /** キャッシュキー（メディアアイテム ID 等）。null で無効化 */
  cacheKey: string | null | undefined;
  waveformUrl?: string | null;
  fallbackSrc?: string | null;
  enabled?: boolean;
}): PeaksResult {
  const { cacheKey, waveformUrl, fallbackSrc, enabled = true } = params;

  const key = cacheKey ?? null;
  const cached = key ? peaksCache.get(key) ?? null : null;

  const [state, setState] = useState<PeaksResult>({
    peaks: cached,
    isLoading: false,
    error: false,
  });

  useEffect(() => {
    if (!enabled || !key) {
      setState({ peaks: null, isLoading: false, error: false });
      return;
    }

    const fromCache = peaksCache.get(key);
    if (fromCache) {
      setState({ peaks: fromCache, isLoading: false, error: false });
      return;
    }

    if (!waveformUrl && !fallbackSrc) {
      setState({ peaks: null, isLoading: false, error: true });
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    setState({ peaks: null, isLoading: true, error: false });

    (async () => {
      try {
        const peaks = await resolvePeaks(waveformUrl, fallbackSrc, controller.signal);
        peaksCache.set(key, peaks);
        if (!cancelled) setState({ peaks, isLoading: false, error: false });
      } catch (e) {
        if (cancelled || controller.signal.aborted) return;
        console.warn('[usePeaks] 波形データの取得に失敗:', e);
        setState({ peaks: null, isLoading: false, error: true });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [enabled, key, waveformUrl, fallbackSrc]);

  return state;
}

/**
 * 複数メディアの波形ピークをまとめて取得する（MA マルチトラックタイムライン等）。
 * キャッシュ・取得ロジックは usePeaks と共有。
 * 返り値はリクエスト key → 結果のマップ（取得完了ごとに更新される）。
 */
export function useMultiPeaks(params: {
  requests: PeaksRequest[];
  enabled: boolean;
}): Record<string, PeaksResult> {
  const { requests, enabled } = params;
  const [results, setResults] = useState<Record<string, PeaksResult>>({});

  // リクエスト内容が実質同じなら effect を再実行しない
  const requestsKey = requests
    .map(r => `${r.key}|${r.waveformUrl ?? ''}|${r.fallbackSrc ?? ''}`)
    .join('\n');

  useEffect(() => {
    if (!enabled || requests.length === 0) {
      setResults({});
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    const initial: Record<string, PeaksResult> = {};
    requests.forEach(r => {
      const cached = peaksCache.get(r.key);
      const hasSource = !!(r.waveformUrl || r.fallbackSrc);
      initial[r.key] = cached
        ? { peaks: cached, isLoading: false, error: false }
        : { peaks: null, isLoading: hasSource, error: !hasSource };
    });
    setResults(initial);

    requests.forEach(r => {
      if (peaksCache.get(r.key)) return;
      if (!r.waveformUrl && !r.fallbackSrc) return;
      (async () => {
        try {
          const peaks = await resolvePeaks(r.waveformUrl, r.fallbackSrc, controller.signal);
          peaksCache.set(r.key, peaks);
          if (!cancelled) {
            setResults(prev => ({ ...prev, [r.key]: { peaks, isLoading: false, error: false } }));
          }
        } catch (e) {
          if (cancelled || controller.signal.aborted) return;
          console.warn('[useMultiPeaks] 波形データの取得に失敗:', e);
          setResults(prev => ({ ...prev, [r.key]: { peaks: null, isLoading: false, error: true } }));
        }
      })();
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // requestsKey がリクエスト配列の内容を表す
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, requestsKey]);

  return results;
}
